/**
 * SchedulesSection — read-only project roll-up of schedules (Schedule Builder, Round 18).
 *
 * Authoring moved under milestones (the spec model): items are created, carried forward, and issued
 * from each schedule-bearing milestone's Schedules section via the Schedule Editor. This branch under
 * Reference → Production is a read-only roll-up of every concept schedule item with its current
 * version status, plus the substitution-request review inbox and the standalone PDF export.
 *
 * Renders as outline rows (no inline table — the table lives in the PDF export).
 */

import { useEffect, useReducer } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { saveViewState } from '../outline/state.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { getScheduleItems, exportSchedulePdf, getScheduleSubstitutions, reviewSubstitution } from '../services/schedules.js'
import { SCHEDULE_TYPES, scheduleSpecFor, tableColumnsForType, tableCellValue } from '../services/scheduleLayout.js'
import { confirmDialog, alertDialog } from './Dialog.jsx'

function loadingRow(text) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
        <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: '#7a9aaa', fontStyle: 'italic' }}>{text}</span></div>
      </div>
    </div>
  )
}

const SUB_STATUS_COLOR = { pending: '#c79a3a', approved: '#1D9E75', rejected: '#c0392b' }
const SUB_STATUS_LABEL = { pending: '⏳ Pending', approved: '✓ Approved', rejected: '✕ Rejected' }

/* A substitution request: proposed change + status, with Approve/Reject when pending. Approving
   applies the proposed values to the live item; issue it via a revision milestone afterward. */
function SubstitutionRow({ sub }) {
  const menu = useActionMenu()
  const color = SUB_STATUS_COLOR[sub.status] || '#7a9aaa'
  const proposedLabel = (sub.proposed && sub.proposed.item_label) || '(unnamed)'
  async function review(decision) {
    const verb = decision === 'approve' ? 'Approve' : 'Reject'
    if (!(await confirmDialog({ title: verb + ' substitution?', message: decision === 'approve' ? 'The item will be updated to the proposed values. Issue it by carrying the item forward into a revision milestone.' : 'The proposed substitution will be rejected.', confirmLabel: verb, danger: decision !== 'approve' }))) return
    reviewSubstitution(sub.id, decision, null)
      .then(() => { ctx.scheduleSubCache = undefined; if (decision === 'approve') ctx.scheduleCache = undefined; window.dispatchEvent(new Event('outline-render')) })
      .catch(e => alertDialog({ title: 'Review failed', message: e.message || String(e) }))
  }
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">↔ {proposedLabel}</span>
          <span className="wf-meta">
            <span style={{ fontSize: 10, color, fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>{SUB_STATUS_LABEL[sub.status] || sub.status}</span>
            {sub.source === 'portal' && <span style={{ fontSize: 10, color: '#7a9aaa', fontFamily: '"DM Sans",sans-serif' }}>from GC</span>}
            {sub.reason && <span style={{ fontSize: 10, color: '#7a9aaa', fontStyle: 'italic' }}>{sub.reason}</span>}
          </span>
        </div>
        {sub.status === 'pending' && <ActionMenuButton menu={menu} />}
      </div>
      {sub.status === 'pending' && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => review('approve')}>Approve substitution</ActionMenuItem>
          <ActionMenuItem onClick={() => review('reject')} danger>Reject substitution</ActionMenuItem>
        </ActionMenu>
      )}
    </div>
  )
}

/* Version badge for the roll-up: Draft (no versions / not in a milestone) | vN | vN · edited. */
function rollupBadge(item) {
  const vers = Array.isArray(item.versions) ? item.versions : []
  const issued = vers.find(v => v.status === 'issued')
  if (!vers.length) return { text: 'Not in a milestone', color: '#9bb2b7' }
  if (!issued) return { text: 'Draft v' + vers[0].version_number, color: '#c79a3a' }
  if (item.edited_since_issue) return { text: 'v' + issued.version_number + ' · edited', color: '#c79a3a' }
  return { text: 'v' + issued.version_number, color: '#7a9aaa' }
}

/* A single settings fact — leaf row, no triangle, matches the loadingRow spacer pattern. */
function SettingRow({ label, value, isUrl }) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{label}</span>
          <span className="wf-meta">
            {isUrl
              ? <a href={value} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#2563eb' }}>link ↗</a>
              : <span style={{ fontSize: 11, color: '#7a9aaa' }}>{value}</span>}
          </span>
        </div>
      </div>
    </div>
  )
}

/* Structural kinds already shown in the item's own header row (label/room/section), or handled by
   their own sub-node (linkedItems) — everything else renders as a flat settings fact when it has a
   value. Source is the SAME SCHEDULE_TABLE_COLUMNS the Schedule Builder itself edits from, so a new
   column added there for any type shows up here automatically, with no changes to this file. */
const STRUCTURAL_KINDS = new Set(['image', 'label', 'section', 'room', 'linkedItems'])
function settingFieldsFor(item) {
  return tableColumnsForType(item.schedule_type)
    .filter(col => !STRUCTURAL_KINDS.has(col.kind))
    .map(col => ({ label: col.label, key: col.key, kind: col.kind, value: tableCellValue(item, col) }))
    .filter(f => f.value)
}
function linkedColumnsFor(item) {
  return tableColumnsForType(item.schedule_type).filter(col => col.kind === 'linkedItems')
}

/* A linkedItems sub-branch under any item (e.g. Hardware on Casework) — expandable, resolves the
   linked {id,label} refs against allItems (already loaded for the whole roll-up) to show each
   linked item's own first settings fact as a hint. A live reference, never a copy — editing the
   linked item elsewhere shows up here too. */
function LinkedItemsNode({ item, col, allItems }) {
  const key = 'sched-item:' + item.id + ':' + col.key
  const collapsed = wf.collapsed[key] !== false
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  const links = (item.attrs && Array.isArray(item.attrs[col.key])) ? item.attrs[col.key] : []
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}><span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <span className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{col.label}</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{links.length}</span>
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {links.length === 0 && loadingRow('None linked yet — add it from the Schedule Builder.')}
          {links.map(l => {
            const full = (allItems || []).find(a => a.id === l.id)
            const hint = full ? (settingFieldsFor(full)[0] || {}).value || '' : ''
            return (
              <div className="wf-node wf-node--item" key={l.id}>
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                  <span className="wf-bullet wf-bullet--item" />
                  <div className="wf-text-wrap">
                    <span className="wf-node-text">{l.label || (full && full.item_label) || '(unnamed)'}</span>
                    {hint && <span className="wf-meta"><span style={{ fontSize: 11, color: '#7a9aaa' }}>{hint}</span></span>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* Any schedule item — expandable: settings as flat facts, plus a sub-branch per linkedItems column
   (e.g. Hardware on Casework). Works uniformly across every schedule type via the shared
   SCHEDULE_TABLE_COLUMNS layout. */
function ScheduleItemRow({ item, allItems }) {
  const key = 'sched-item:' + item.id
  const collapsed = wf.collapsed[key] !== false
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  const spec = scheduleSpecFor(item.schedule_type)
  const group = spec.groupBy === 'spec_section' ? item.spec_section : item.room_string
  const badge = rollupBadge(item)
  const issued = (item.versions || []).find(v => v.status === 'issued')
  const label = item.item_label || item.room_string || item.spec_section || '(unnamed)'
  const settings = settingFieldsFor(item)
  const linkedCols = linkedColumnsFor(item)
  return (
    <div className={`wf-node wf-node--item${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}><span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{label}</span>
          <span className="wf-meta">
            {group && group !== label && <span style={{ fontSize: 10, color: '#7a9aaa' }}>{group}</span>}
            <span style={{ fontSize: 10, color: badge.color, fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>{badge.text}</span>
            {issued && issued.milestone_name && <span style={{ fontSize: 10, color: '#7a9aaa' }}>{issued.milestone_name}</span>}
          </span>
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {settings.length === 0 && linkedCols.length === 0 && loadingRow('No details set yet.')}
          {settings.map(s => <SettingRow key={s.key} label={s.label} value={s.value} isUrl={s.kind === 'url'} />)}
          {linkedCols.map(col => <LinkedItemsNode key={col.key} item={item} col={col} allItems={allItems} />)}
        </div>
      )}
    </div>
  )
}

/* One schedule-type group node (read-only). */
function ScheduleTypeNode({ type, items, allItems }) {
  const key = 'sched-type:' + type
  const collapsed = wf.collapsed[key] !== false
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}><span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <span className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{type}</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{items.length}</span>
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {items.map(it => <ScheduleItemRow key={it.id} item={it} allItems={allItems} />)}
        </div>
      )}
    </div>
  )
}

/* Substitution-request review inbox (collapsible). */
function SubstitutionsNode({ subs }) {
  const key = 'sched-subs'
  const collapsed = wf.collapsed[key] !== false
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  const pending = subs.filter(s => s.status === 'pending').length
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}><span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <span className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Substitution requests</span>
          {pending > 0 && <span className="wf-meta" style={{ fontSize: 10, color: '#c79a3a', fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>↔ {pending} to review</span>}
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {subs.length === 0 && loadingRow('No substitution requests.')}
          {subs.map(s => <SubstitutionRow key={s.id} sub={s} />)}
        </div>
      )}
    </div>
  )
}

export default function SchedulesSection({ projectId, conceptId }) {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const key = 'sec:prod:schedules'
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const cacheKey = (projectId || '') + '|' + (conceptId || '')

  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  useEffect(() => {
    function load() {
      if (collapsed || !projectId) return
      const c = ctx.scheduleCache
      if (!(c && c.key === cacheKey && c.items !== undefined)) {
        ctx.scheduleCache = { key: cacheKey, items: null }
        getScheduleItems(projectId, conceptId)
          .then(list => { ctx.scheduleCache = { key: cacheKey, items: list }; window.dispatchEvent(new Event('outline-render')) })
          .catch(() => { ctx.scheduleCache = { key: cacheKey, items: [] }; window.dispatchEvent(new Event('outline-render')) })
      }
      const s = ctx.scheduleSubCache
      if (!(s && s.key === cacheKey && s.subs !== undefined)) {
        ctx.scheduleSubCache = { key: cacheKey, subs: null }
        getScheduleSubstitutions(projectId, conceptId)
          .then(list => { ctx.scheduleSubCache = { key: cacheKey, subs: list }; window.dispatchEvent(new Event('outline-render')) })
          .catch(() => { ctx.scheduleSubCache = { key: cacheKey, subs: [] }; window.dispatchEvent(new Event('outline-render')) })
      }
    }
    window.addEventListener('outline-render', load)
    load()
    return () => window.removeEventListener('outline-render', load)
  }, [collapsed, cacheKey, projectId, conceptId])

  const c = ctx.scheduleCache
  const items = (c && c.key === cacheKey) ? c.items : undefined
  const subState = ctx.scheduleSubCache
  const subs = (subState && subState.key === cacheKey && Array.isArray(subState.subs)) ? subState.subs : []
  const pendingSubs = subs.filter(s => s.status === 'pending').length

  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  function exportPdf() {
    const projectName = ctx.state?.milestones?.[0]?.fields?.['Project Name'] || 'Schedules'
    // Open the preview tab synchronously (dodge popup blockers); point it at the PDF when ready.
    const win = window.open('', '_blank')
    if (win) { try { win.document.write('<!doctype html><title>Preparing schedule preview…</title><body style="font:14px system-ui;color:#5b8494;padding:24px">Preparing schedule preview…</body>') } catch {} }
    exportSchedulePdf(projectId, conceptId, projectName, { targetWindow: win, preview: true })
      .catch(e => { if (win && !win.closed) win.close(); alertDialog({ title: 'Export failed', message: e.message || String(e) }) })
  }
  function setAll(expand) {
    wf.collapsed[key] = !expand
    SCHEDULE_TYPES.forEach(t => { wf.collapsed['sched-type:' + t] = !expand })
    wf.collapsed['sched-subs'] = !expand
    saveViewState(); window.dispatchEvent(new Event('outline-render'))
  }

  const byType = {}
  if (Array.isArray(items)) for (const it of items) { (byType[it.schedule_type] = byType[it.schedule_type] || []).push(it) }
  const presentTypes = SCHEDULE_TYPES.filter(t => byType[t]).concat(
    Object.keys(byType).filter(t => !SCHEDULE_TYPES.includes(t))
  )
  const total = Array.isArray(items) ? items.length : null

  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}><span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <span className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Schedules</span>
          {total != null && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{total}</span>}
          {pendingSubs > 0 && <span className="wf-meta" style={{ fontSize: 10, color: '#c79a3a', fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>↔ {pendingSubs} to review</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={exportPdf}>Export schedule (PDF)</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {!conceptId && loadingRow('No approved concept — schedules are concept-scoped.')}
          {conceptId && (items === undefined || items === null) && loadingRow('Loading…')}
          {conceptId && Array.isArray(items) && items.length === 0 && loadingRow('No schedule items yet — add them from a milestone’s Schedules section.')}
          {conceptId && Array.isArray(items) && (
            <>
              {presentTypes.map(t => <ScheduleTypeNode key={t} type={t} items={byType[t]} allItems={items} />)}
              <SubstitutionsNode subs={subs} />
            </>
          )}
        </div>
      )}
    </div>
  )
}
